import React, { useEffect, useRef, useState } from "react";
import { MinusIcon } from "@heroicons/react/20/solid";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import CellSaveStatusIndicator from "../../controls/CellSaveStatusIndicator";
// @ts-expect-error The existing JavaScript utility module has no type declarations.
import { convertToCurrencyFormat, isSourceItemReturned, mapSourceItemTaxes, sumSourceItemTaxRates } from "../../../shared/utility";
import type { PricingMutationResult, PricingSaveStatus, SourceItemPricingChoice } from "./summaryPricingTypes";

type SummaryTaxIncludedCheckboxProps = {
  group: { key: string; sourceItems?: SourceItemPricingChoice[] };
  productLabel: string;
  disabled?: boolean;
  onCommit?: (sourceIds: number[], taxIncluded: boolean) => Promise<PricingMutationResult | undefined>;
};

function taxRateFor(item: SourceItemPricingChoice): number | null {
  if (item.tax_rate != null) return Number(item.tax_rate);
  const taxes = mapSourceItemTaxes(item);
  if (taxes.length) return sumSourceItemTaxRates(taxes);
  return null;
}

export default function SummaryTaxIncludedCheckbox({ group, productLabel, disabled = false, onCommit }: SummaryTaxIncludedCheckboxProps) {
  const checkbox = useRef<HTMLInputElement>(null);
  const pending = useRef(false);
  const [status, setStatus] = useState<PricingSaveStatus>("idle");
  const saving = status === "saving";
  const [errorMessage, setErrorMessage] = useState("");
  const [retryValue, setRetryValue] = useState<boolean | null>(null);
  const [optimisticValue, setOptimisticValue] = useState<boolean | null>(null);
  const sourceItems = (group.sourceItems || []).filter((item) => !isSourceItemReturned(item));
  const taxableItems = sourceItems.filter((item) => taxRateFor(item) !== 0);
  const hasSavedFlags = sourceItems.some((item) => Boolean(item.tax_included));
  const stateItems = taxableItems.length ? taxableItems : sourceItems;
  const allChecked = stateItems.length > 0 && stateItems.every((item) => Boolean(item.tax_included));
  const someChecked = stateItems.some((item) => Boolean(item.tax_included));
  const includedTaxAmount = taxableItems.reduce(
    (total, item) => total + (item.tax_included ? Number(item.tax_amount || 0) : 0),
    0,
  );
  const indeterminate = someChecked && !allChecked;
  const checked = optimisticValue ?? (allChecked || indeterminate);
  const mixed = optimisticValue === null && indeterminate;
  const hasCurrentTaxAmount = optimisticValue === null || (!indeterminate && allChecked === optimisticValue);
  const disabledForExemption = taxableItems.length === 0 && !hasSavedFlags;
  const sourceIds = sourceItems.map((item) => item.id);
  const inputId = `summary-tax-input-${group.key}`;

  useEffect(() => {
    if (checkbox.current) checkbox.current.indeterminate = mixed;
  }, [mixed, saving]);

  useEffect(() => {
    // Other row saves can refresh this row before its own refreshed data arrives.
    // Keep the user's choice visible until the saved snapshot confirms it.
    if (!saving && optimisticValue !== null && !indeterminate && allChecked === optimisticValue) {
      setOptimisticValue(null);
    }
  }, [allChecked, indeterminate, optimisticValue, saving]);

  useEffect(() => {
    if (status !== "saved") return;
    const timeout = setTimeout(() => setStatus("idle"), 3000);
    return () => clearTimeout(timeout);
  }, [status]);

  const save = async (nextValue: boolean) => {
    if (pending.current || disabled || !onCommit || sourceIds.length === 0) return;
    pending.current = true;
    setRetryValue(nextValue);
    setOptimisticValue(nextValue);
    setStatus("saving");
    setErrorMessage("");
    try {
      const result = await onCommit(sourceIds, nextValue);
      if (!result?.success) {
        setOptimisticValue(null);
        setStatus("error");
        setErrorMessage(result?.data?.message || "Could not update Tax included. Retry the row.");
        return;
      }
      if (result.data?.skipped_ids?.length) setOptimisticValue(null);
      setStatus("saved");
    } catch {
      setOptimisticValue(null);
      setStatus("error");
      setErrorMessage("Could not update Tax included. Retry the row.");
    } finally {
      pending.current = false;
    }
  };

  const setTaxIncluded = (event: CheckboxChangeEvent) => {
    // Mixed rows enable on the first click; exempt rows can only clear saved flags.
    save(taxableItems.length === 0 ? false : mixed || Boolean(event.checked));
  };

  return (
    <div className="space-y-1" aria-busy={saving}>
      <div className="flex items-center gap-1">
        <label htmlFor={inputId} className="inline-flex min-h-11 min-w-11 items-center justify-center md:min-h-9 md:min-w-9">
          <Checkbox
            inputRef={checkbox}
            inputId={inputId}
            aria-label={`Tax included for ${productLabel}`}
            aria-describedby={`summary-tax-${group.key}`}
            aria-checked={mixed ? "mixed" : checked}
            checked={checked}
            icon={mixed ? <MinusIcon className="h-3 w-3" /> : undefined}
            disabled={disabled || saving || disabledForExemption || sourceIds.length === 0}
            onChange={setTaxIncluded}
            title={disabledForExemption ? "Tax-exempt row" : undefined}
          />
        </label>
        {checked && hasCurrentTaxAmount ? (
          <span className="whitespace-nowrap text-sm tabular-nums text-gray-700" aria-label={`Included tax for ${productLabel}`}>
            {convertToCurrencyFormat(includedTaxAmount, 2)}
          </span>
        ) : null}
        <CellSaveStatusIndicator status={status} />
      </div>
      <div id={`summary-tax-${group.key}`} className="flex min-h-4 items-center gap-1 text-xs">
        <span role={status === "error" ? "alert" : "status"} className={status === "error" ? "text-red-700" : "text-gray-600"}>
          {errorMessage}
        </span>
      </div>
      {status === "error" ? (
        <button type="button" onClick={() => save(retryValue ?? (taxableItems.length === 0 ? false : !checked))} disabled={saving || disabled} className="min-h-11 text-xs text-indigo-700 underline md:min-h-8">
          Retry tax update
        </button>
      ) : null}
    </div>
  );
}
